import { computed, onBeforeUnmount, useContext, onMounted, reactive, ref, defineComponent, useCssModule, watch, provide, } from '@nuxtjs/composition-api';
import { throttle } from 'throttle-debounce-ts';
import { ResizeObserver } from 'resize-observer';
import { useTask } from 'vue-concurrency';
import { useDuck } from '~/services';
import { createShareImage } from '~/utils';
import { useA11y } from '~/composables';
import { ADFOX_CODES_MOBILE } from '~/components/Adfox';
import { CLUB_PROMO } from '~/pages/club/data';
import { useRecommendationSystem } from '~/composables/useRecommendationSystem';
import { useGlobalScrollTracker } from '~/composables/globalScrollTracker';
export default defineComponent({
    name: 'TheArticle',
    props: {
        article: {
            required: true,
            type: Object,
        },
        index: {
            default: 0,
            type: Number,
        },
        isFolded: {
            type: Boolean,
        },
        schemaDisabled: {
            default: false,
            type: Boolean,
        },
        paywall: {
            default: null,
            type: Object,
        },
        readingIndex: {
            default: -1,
            type: Number,
        },
    },
    emits: ['loadMore', 'unfold', 'updateReading'],
    setup(props) {
        /**
         * Accessibility
         */
        const a11y = useA11y();
        /**
         * CSS Modules.
         */
        const css = useCssModule();
        /**
         * Context.
         */
        const context = useContext();
        /**
         * Duck service.
         */
        const duck = useDuck();
        /**
         * Recommendation service.
         */
        const recommendationSystem = useRecommendationSystem();
        /**
         * Max scroll view value.
         */
        const { scrollY } = useGlobalScrollTracker();
        /**
         * Environment variables.
         */
        const env = context.env;
        /**
         * Adfox loader
         */
        const adfoxLoader = context.$adfoxLoader;
        /**
         * Viewport.
         */
        const viewport = context.$viewport;
        /**
         * Actions start.
         */
        const contentElement = ref();
        /**
         * Root element.
         */
        const rootElement = ref();
        /**
         * Partner items.
         */
        const partnerItems = ref([]);
        /**
         * Recommendation Articles items.
         */
        const recommendationArticlesList = ref([]);
        /**
         * Points depth view.
         */
        const viewDepthPoints = ref({
            entered: false,
            currently: false,
            finished: false,
        });
        const maxScrollValue = ref(scrollY.value);
        /**
         * Time start view material.
         */
        const timeToStartView = ref(0);
        const isRating = computed(() => Boolean(props.article?.rating));
        const rubricType = computed(() => {
            if (props.article?.sections) {
                return props.article?.sections[0]?.url?.split('/')[0] || 'other';
            }
            return 'other';
        });
        const shouldHideImage = computed(() => {
            const { hide_image: isHidden, withVideo } = props.article;
            return isRating.value || withVideo || isHidden || (a11y.isEnabled && !a11y.modes.images.isEnabled);
        });
        const currentUrl = computed(() => `${env.HOST}/${props.article.url_alias}`);
        const previousScroll = ref(0);
        const adfoxPartner = {
            type: 'ad',
            data: {
                bannerName: 'articlePartner',
                config: ADFOX_CODES_MOBILE.articlePartner,
            },
        };
        /**
         * Actions.
         */
        const actions = reactive({
            anchorArea: [0, 0],
            anchorObserver: null,
            animationFrameId: 0,
            areVisible: false,
            items: [
                {
                    copy: {
                        withText: viewport.breakpoint === 'desktopWide',
                    },
                },
                {
                    share: {
                        url: props.article.erid ? currentUrl.value + `?erid=${props.article.erid}` : currentUrl.value,
                        vkImage: createShareImage({
                            article: props.article,
                            env,
                            isVk: true,
                        }),
                    },
                },
                {
                    bookmark: {
                        article: props.article.url_alias,
                        tooltip: true,
                    },
                },
                {
                    speech: {
                        tooltip: true,
                    },
                },
                {
                    a11y: {
                        tooltip: {
                            gap: '18px',
                            position: 'bottom',
                        },
                    },
                },
            ],
            theme: computed(() => (a11y.isEnabled ? 'a11y' : 'light')),
        });
        /**
         * Floating actions.
         */
        const floatingActions = reactive({
            actions: [
                {
                    speech: {
                        tooltip: true,
                    },
                },
                {
                    share: {
                        tooltipJustify: 'end',
                        tooltipPosition: 'top',
                        url: props.article.erid ? currentUrl.value + `?erid=${props.article.erid}` : currentUrl.value,
                        vkImage: createShareImage({
                            article: props.article,
                            env,
                            isVk: true,
                        }),
                        withCopyLink: true,
                    },
                },
                {
                    bookmark: {
                        article: props.article.url_alias,
                        tooltip: true,
                    },
                },
            ],
            size: 'small',
            theme: 'dark',
        });
        const fetchPartnerTask = useTask(function* () {
            try {
                const { data } = yield duck.pub.getPubListById('partners');
                if (!data) {
                    return;
                }
                partnerItems.value = data.articles.map((article) => article.data) || [];
            }
            catch (error) {
                console.error(error);
            }
        });
        /**
         * Handles window "scroll" event.
         */
        const handleWindowScroll = throttle(60, () => {
            cancelAnimationFrame(actions.animationFrameId);
            actions.animationFrameId = requestAnimationFrame(() => {
                const [top, bottom] = actions.anchorArea;
                const scrollOffset = window.scrollY;
                actions.areVisible = scrollOffset >= top && scrollOffset <= bottom;
                if (viewport.isLessThan('tablet')) {
                    if (actions.areVisible) {
                        actions.areVisible = previousScroll.value > scrollOffset;
                    }
                }
                previousScroll.value = scrollOffset;
            });
        });
        function handlePartnerIntersect() {
            if (fetchPartnerTask.isRunning || partnerItems.value.length) {
                return;
            }
            fetchPartnerTask.perform();
        }
        watch(() => scrollY.value, (newScrollY) => {
            if (newScrollY > maxScrollValue.value) {
                maxScrollValue.value = newScrollY;
            }
        });
        const getViewPercentage = () => {
            const { height, top } = rootElement.value.getBoundingClientRect();
            const absoluteTop = top + window.scrollY;
            const viewportHeight = window.innerHeight;
            const maxViewportBottom = maxScrollValue.value + viewportHeight;
            const pixelsScrolledInside = maxViewportBottom - absoluteTop;
            const viewPercentage = (pixelsScrolledInside / height) * 100;
            const correctViewPercentage = Math.min(100, Math.max(0, viewPercentage)).toFixed(0);
            return Number(correctViewPercentage);
        };
        function handleRecommendation(pointName, scrollDepth = 1) {
            if (viewDepthPoints.value[pointName] || !rootElement.value.getBoundingClientRect().height) {
                return;
            }
            viewDepthPoints.value[pointName] = true;
            const timeNow = new Date().getTime();
            const duration = timeToStartView.value
                ? Math.abs(timeNow - timeToStartView.value)
                : ((timeToStartView.value = timeNow), 1);
            const id = props.article.id;
            recommendationSystem?.sendView({ id, duration, scrollDepth });
        }
        onBeforeUnmount(() => {
            const scrollDepth = getViewPercentage();
            if (scrollDepth > 1 && !viewDepthPoints.value.finished) {
                handleRecommendation('currently', scrollDepth);
            }
            window.removeEventListener('scroll', handleWindowScroll);
            actions.anchorObserver?.disconnect();
        });
        onMounted(async () => {
            if (!contentElement.value) {
                return;
            }
            actions.anchorObserver = new ResizeObserver(([entry]) => {
                const target = entry.target;
                const { bottom, top } = target.getBoundingClientRect();
                const scrollOffset = window.scrollY - 56;
                actions.anchorArea = [top + scrollOffset, bottom + scrollOffset];
            });
            actions.anchorObserver.observe(contentElement.value);
            window.addEventListener('scroll', handleWindowScroll);
            handleWindowScroll();
            watch(() => viewport.breakpoint, () => {
                const copyIndex = actions.items.findIndex((action) => action.copy);
                const copyAction = actions.items[copyIndex];
                copyAction.copy.withText = viewport.breakpoint === 'desktopWide';
            });
        });
        provide('article', props.article);
        return {
            CLUB_PROMO,
            a11y,
            actions,
            adfoxLoader,
            adfoxPartner,
            contentElement,
            currentUrl,
            css,
            floatingActions,
            isRating,
            rubricType,
            handlePartnerIntersect,
            handleRecommendation,
            partnerItems,
            rootElement,
            shouldHideImage,
            recommendationArticlesList,
        };
    },
});
